import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Footer, MarketingHeader, SEO } from '@/components';
import { ChildSupportCalculator } from '@/components/child-support/ChildSupportCalculator';
import { useIsDivorceProductEnabled } from '@/hooks/usePublicFeatureFlags';
import { track } from '@/lib/analytics';
import {
  getChildSupportPageDescription,
  getChildSupportPageTitle,
  getStateConfigBySlug,
  isStateSlug,
} from '@/lib/child-support/schemas';

export const Route = createFileRoute('/child-support-calculator/$state')({
  beforeLoad: ({ params }) => {
    if (!isStateSlug(params.state)) {
      throw redirect({ to: '/child-support-calculator' });
    }
  },
  component: ChildSupportStatePage,
});

function ChildSupportStatePage() {
  const { state } = Route.useParams();
  const stateConfig = getStateConfigBySlug(state);
  const divorceEnabled = useIsDivorceProductEnabled();
  if (!stateConfig) return null;

  useEffect(() => {
    track('Child Support State Page View', { state });
  }, [state]);

  const path = `/child-support-calculator/${state}`;
  const pageUrl = `https://harborcoparent.com${path}`;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://harborcoparent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Child Support Calculator',
        item: 'https://harborcoparent.com/child-support-calculator',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${stateConfig.name} Calculator`,
        item: pageUrl,
      },
    ],
  };

  const appJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: `${stateConfig.name} Child Support Calculator`,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    url: pageUrl,
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title={`${getChildSupportPageTitle(stateConfig)} | Harbor`}
        description={getChildSupportPageDescription(stateConfig)}
        path={path}
        jsonLd={[breadcrumbJsonLd, appJsonLd]}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-10'>
        <div className='mx-auto max-w-4xl px-6'>
          <h1 className='mb-3 text-4xl font-bold text-gray-900 md:text-5xl'>
            {stateConfig.name} child support calculator
          </h1>
          <p className='text-lg leading-relaxed text-gray-600 md:text-xl'>
            Quick estimate for {stateConfig.name} based on income and parenting time. This
            calculator is an educational model only and not legal advice.
          </p>
        </div>
      </section>

      <div className='mx-auto max-w-7xl px-6 py-12'>
        <ChildSupportCalculator defaultStateSlug={state} />
      </div>

      {divorceEnabled ? (
        <section className='mx-auto max-w-4xl px-6 pb-12 text-center'>
          <p className='text-gray-600'>
            Going through a divorce in {stateConfig.name}?{' '}
            <Link
              to='/divorce/$state'
              params={{ state }}
              onClick={() => track('Child Support Divorce CTA Clicked', { state })}
              className='font-semibold text-primary hover:underline'
            >
              See the step-by-step {stateConfig.name} divorce checklist
            </Link>
            {' '}: official forms, filing fees, and fee waivers.
          </p>
        </section>
      ) : null}

      <Footer />
    </div>
  );
}
